import React, { useEffect, useRef, useState, useMemo } from 'react';
import { createPortal } from 'react-dom';
import { useIntl } from 'react-intl';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import { useCurrency } from '../../hooks/useCurrency';
import { defaultMapCenter } from '../../lib/defaultMapCenter';

// Province/state/region centroid coordinates used by the Leaflet heatmap.
// Add new entries here when onboarding a new country's regional grouping.
const PROVINCE_COORDINATES = {
  // Canadian Provinces
  "Ontario": [51.2538, -85.3232],
  "Quebec": [52.9399, -73.5491],
  "British Columbia": [53.7267, -127.6476],
  "Alberta": [55.0000, -115.0000],
  "Manitoba": [55.1833, -97.8333],
  "Saskatchewan": [55.0000, -106.0000],
  "Nova Scotia": [45.0000, -63.0000],
  "New Brunswick": [46.4984, -66.1597],
  "Newfoundland and Labrador": [53.1355, -57.6604],
  "Prince Edward Island": [46.5107, -63.4168],
  // US States
  "Alabama": [32.7794, -86.8287],
  "Alaska": [64.0685, -152.2782],
  "Arizona": [34.2744, -111.6602],
  "Arkansas": [34.8938, -92.4426],
  "California": [36.7783, -119.4179],
  "Colorado": [39.5501, -105.7821],
  "Connecticut": [41.5928, -72.7074],
  "Delaware": [38.9896, -75.5050],
  "District of Columbia": [38.9072, -77.0369],
  "Florida": [27.9944, -81.7603],
  "Georgia": [32.6415, -83.4426],
  "Hawaii": [20.2927, -156.3737],
  "Idaho": [44.0682, -114.7420],
  "Illinois": [40.0417, -89.1965],
  "Indiana": [39.8942, -86.2816],
  "Iowa": [42.0751, -93.4960],
  "Kansas": [38.4937, -98.3200],
  "Kentucky": [37.5347, -85.3021],
  "Louisiana": [31.0689, -91.9968],
  "Maine": [45.3695, -69.2428],
  "Maryland": [39.0550, -76.7909],
  "Massachusetts": [42.2596, -71.8083],
  "Michigan": [44.3467, -85.4102],
  "Minnesota": [46.2807, -94.3053],
  "Mississippi": [32.7364, -89.6678],
  "Missouri": [38.4623, -92.3020],
  "Montana": [47.0527, -109.6333],
  "Nebraska": [41.5378, -99.7951],
  "Nevada": [39.3289, -116.6312],
  "New Hampshire": [43.6805, -71.5811],
  "New Jersey": [40.1907, -74.6728],
  "New Mexico": [34.4071, -106.1126],
  "New York": [42.9538, -75.5268],
  "North Carolina": [35.5557, -79.3877],
  "North Dakota": [47.4501, -100.4659],
  "Ohio": [40.2862, -82.7937],
  "Oklahoma": [35.5889, -97.4943],
  "Oregon": [43.9336, -120.5583],
  "Pennsylvania": [40.8781, -77.7996],
  "Rhode Island": [41.6762, -71.5562],
  "South Carolina": [33.9169, -80.8964],
  "South Dakota": [44.4443, -100.2263],
  "Tennessee": [35.8600, -86.3505],
  "Texas": [31.4757, -99.3312],
  "Utah": [39.3055, -111.6703],
  "Vermont": [44.0687, -72.6658],
  "Virginia": [37.5215, -78.8537],
  "Washington": [47.3826, -120.4472],
  "West Virginia": [38.6409, -80.6227],
  "Wisconsin": [44.6243, -89.9941],
  "Wyoming": [43.0799, -107.6893],
  // UK — SJP regional carve-up (see config/uk_postcode_regions.yml)
  "London": [51.5074, -0.1278],
  "South East": [51.28, -0.78],
  "South West": [50.78, -3.99],
  "Midlands": [52.48, -1.89],
  "North": [53.80, -1.55],
  "Scotland": [56.49, -4.20],
  "Wales": [52.13, -3.78],
  "NI": [54.79, -6.49],
  "Other UK": [54.0, -2.5],
  // Other regions
  "Aragon": [41.5976, -0.9057],
  "Brittany": [48.2020, -2.9326],
  "Harju County": [59.3396, 25.1332],
};

const buildCurrencyFormatter = ({ code, locale }, options) =>
  new Intl.NumberFormat(locale || 'en-US', {
    style: 'currency',
    currency: code || 'USD',
    ...options,
  });

// Read --succession-primary from CSS variables, fallback to default brand blue
const getBrandRgb = () => {
  try {
    const hex = getComputedStyle(document.documentElement)
      .getPropertyValue('--succession-primary')
      .trim()
      .replace('#', '');
    if (hex.length === 6) {
      return {
        r: parseInt(hex.slice(0, 2), 16),
        g: parseInt(hex.slice(2, 4), 16),
        b: parseInt(hex.slice(4, 6), 16),
      };
    }
  } catch (_) {}
  return { r: 84, g: 137, b: 182 }; // fallback #5489B6
};

// Blend from a very light tint (ratio=0) to full brand color (ratio=1)
const getHeatColor = (ratio, brand) => {
  const lr = Math.round(brand.r * 0.15 + 255 * 0.85);
  const lg = Math.round(brand.g * 0.15 + 255 * 0.85);
  const lb = Math.round(brand.b * 0.15 + 255 * 0.85);
  const cr = Math.round(lr + (brand.r - lr) * ratio);
  const cg = Math.round(lg + (brand.g - lg) * ratio);
  const cb = Math.round(lb + (brand.b - lb) * ratio);
  return `rgb(${cr}, ${cg}, ${cb})`;
};

const getBubbleRadius = (aum, maxAum) => {
  if (!aum || !maxAum) return 8;
  const min = 8;
  const max = 38;
  return Math.max(min, Math.min(max, (aum / maxAum) * (max - min) + min));
};

const HeatmapTooltip = ({ tooltip }) => {
  const intl = useIntl();
  const currency = useCurrency();
  const formatCurrencyFull = (value) =>
    buildCurrencyFormatter(currency, { minimumFractionDigits: 0, maximumFractionDigits: 0 }).format(value || 0);
  if (!tooltip || typeof document === 'undefined') return null;

  return createPortal(
    <div
      className="fixed pointer-events-none"
      style={{
        left: tooltip.x + 14,
        top: tooltip.y - 10,
        zIndex: 2147483647,
        transform: 'translateZ(0)',
      }}
    >
      <div className="rounded-lg border border-succession-border bg-succession-bg-primary shadow-succession-lg px-3 py-2.5 min-w-[180px]">
        <p className="text-sm font-semibold text-succession-text-primary mb-1.5">{tooltip.region}</p>
        <div className="space-y-1">
          <div className="flex justify-between gap-4 text-xs">
            <span className="text-succession-text-secondary">{intl.formatMessage({ id: 'succession.launchpad.heatmap_tooltip_sellers' })}</span>
            <span className="font-medium text-succession-text-primary">{tooltip.count}</span>
          </div>
          <div className="flex justify-between gap-4 text-xs">
            <span className="text-succession-text-secondary">{intl.formatMessage({ id: 'succession.launchpad.heatmap_tooltip_aum' })}</span>
            <span className="font-medium text-succession-text-primary">{formatCurrencyFull(tooltip.aum)}</span>
          </div>
          <div className="flex justify-between gap-4 text-xs">
            <span className="text-succession-text-secondary">{intl.formatMessage({ id: 'succession.launchpad.heatmap_tooltip_gp' })}</span>
            <span className="font-medium text-succession-text-primary">{formatCurrencyFull(tooltip.gp)}</span>
          </div>
        </div>
      </div>
    </div>,
    document.body
  );
};

const SellerHeatMapLaunchpad = ({ data, loading, error }) => {
  const intl = useIntl();
  const mapContainerRef = useRef(null);
  const mapRef = useRef(null);
  const [tooltip, setTooltip] = useState(null); // { x, y, region, aum, gp, count }
  const currency = useCurrency();
  const formatCurrency = (value) =>
    buildCurrencyFormatter(currency, { notation: 'compact', maximumFractionDigits: 1 }).format(value || 0);
  const formatCurrencyFull = (value) =>
    buildCurrencyFormatter(currency, { minimumFractionDigits: 0, maximumFractionDigits: 0 }).format(value || 0);
  const initialCenter = defaultMapCenter(typeof window !== 'undefined' ? window.country : null);

  const entries = useMemo(() => Object.entries(data || {}), [data]);
  const maxAum = useMemo(
    () => Math.max(...entries.map(([, v]) => v.assets_under_management || 0), 1),
    [entries]
  );
  const totalAum = useMemo(
    () => entries.reduce((sum, [, v]) => sum + (v.assets_under_management || 0), 0),
    [entries]
  );
  const top5 = useMemo(
    () =>
      [...entries]
        .sort(([, a], [, b]) => (b.assets_under_management || 0) - (a.assets_under_management || 0))
        .slice(0, 5),
    [entries]
  );

  useEffect(() => {
    if (loading || error || !mapContainerRef.current || entries.length === 0) return;

    const brand = getBrandRgb();

    if (!mapRef.current) {
      mapRef.current = L.map(mapContainerRef.current, { zoomControl: true, scrollWheelZoom: false })
        .setView([initialCenter.lat, initialCenter.lng], initialCenter.zoom);

      L.tileLayer(
        'https://{s}.basemaps.cartocdn.com/dark_all/{z}/{x}/{y}{r}.png',
        {
          attribution:
            '&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors &copy; <a href="https://carto.com/attributions">CARTO</a>',
          subdomains: 'abcd',
          maxZoom: 19,
        }
      ).addTo(mapRef.current);
    }

    // Ensure Leaflet panes/controls stay below our React portal tooltip.
    mapRef.current.getPanes().popupPane.style.zIndex = '700';
    mapRef.current.getPanes().tooltipPane.style.zIndex = '700';
    mapRef.current.getPanes().markerPane.style.zIndex = '600';
    mapRef.current.getPanes().overlayPane.style.zIndex = '500';

    // Remove existing circle markers
    mapRef.current.eachLayer((layer) => {
      if (layer instanceof L.CircleMarker) mapRef.current.removeLayer(layer);
    });

    entries.forEach(([region, values]) => {
      const coords = PROVINCE_COORDINATES[region];
      if (!coords) return;

      const aum = values.assets_under_management || 0;
      const ratio = maxAum > 0 ? aum / maxAum : 0;
      const color = getHeatColor(ratio, brand);
      const radius = getBubbleRadius(aum, maxAum);

      const marker = L.circleMarker(coords, {
        radius,
        fillColor: color,
        color: 'rgba(255,255,255,0.25)',
        weight: 1,
        opacity: 0.9,
        fillOpacity: 0.85,
      }).addTo(mapRef.current);

      marker.on('mouseover', (e) => {
        marker.setStyle({ weight: 2, color: 'rgba(255,255,255,0.8)' });
        setTooltip({
          x: e.originalEvent.clientX,
          y: e.originalEvent.clientY,
          region,
          aum,
          gp: values.gross_production || 0,
          count: values.sellers_count || 0,
        });
      });

      marker.on('mousemove', (e) => {
        setTooltip((prev) =>
          prev ? { ...prev, x: e.originalEvent.clientX, y: e.originalEvent.clientY } : prev
        );
      });

      marker.on('mouseout', () => {
        marker.setStyle({ weight: 1, color: 'rgba(255,255,255,0.25)' });
        setTooltip(null);
      });
    });

    return () => {};
  }, [data, loading, error, entries, maxAum]);

  // Cleanup map on unmount
  useEffect(() => {
    return () => {
      if (mapRef.current) {
        mapRef.current.remove();
        mapRef.current = null;
      }
    };
  }, []);

  if (loading) {
    return (
      <div className="rounded-xl border border-succession-border bg-succession-bg-secondary p-6 animate-pulse">
        <div className="h-4 bg-succession-bg-tertiary rounded w-48 mb-4"></div>
        <div className="h-64 bg-succession-bg-tertiary rounded"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="rounded-xl border border-succession-border bg-succession-bg-secondary p-6">
        <p className="text-succession-error text-sm">Unable to load seller heatmap.</p>
      </div>
    );
  }

  return (
    <div className="rounded-xl border border-succession-border bg-succession-bg-secondary overflow-hidden">
      {/* Header */}
      <div className="px-4 py-3 border-b border-succession-border">
        <h4 className="text-sm font-semibold text-succession-text-primary">{intl.formatMessage({ id: 'succession.launchpad.heatmap_header_title' })}</h4>
        <p className="text-xs text-succession-text-secondary mt-0.5">
          {intl.formatMessage(
            { id: 'succession.launchpad.heatmap_header_total' },
            {
              total: <span className="font-medium text-succession-text-primary">{formatCurrencyFull(totalAum)}</span>,
              count: entries.length,
              regionWord: intl.formatMessage({ id: entries.length !== 1 ? 'succession.launchpad.heatmap_region_plural' : 'succession.launchpad.heatmap_region_singular' }),
            },
          )}
        </p>
      </div>

      {/* Map + Top-5 overlay. Leaflet tile pane is z-400, so z-[1000] wins. */}
      <div className="relative">
        <div ref={mapContainerRef} style={{ height: '320px', width: '100%' }} />

        {/* Top-5 overlay — inline styles used for zIndex + background so Tailwind JIT
             / stacking-context quirks around Leaflet tile panes can't hide it. */}
        {top5.length > 0 && (
          <div
            className="absolute rounded-lg border border-succession-border shadow-succession-md p-3 min-w-[180px]"
            style={{
              top: '12px',
              right: '12px',
              zIndex: 9999,
              backgroundColor: '#ffffff',
              isolation: 'isolate',
            }}
          >
            <p className="text-xs font-semibold text-succession-text-primary mb-2">{intl.formatMessage({ id: 'succession.launchpad.heatmap_top_regions_by_aum' })}</p>
            <ol className="space-y-1.5">
              {top5.map(([region, values], idx) => (
                <li key={region} className="flex items-center gap-2">
                  <span className="text-xs font-bold text-succession-text-muted w-4 shrink-0">{idx + 1}.</span>
                  <div className="min-w-0">
                    <p className="text-xs font-medium text-succession-text-primary truncate">{region}</p>
                    <p className="text-xs text-succession-text-secondary">{formatCurrency(values.assets_under_management)}</p>
                  </div>
                </li>
              ))}
            </ol>
          </div>
        )}
      </div>

      {/* Legend row — rendered below the map as a footer, outside Leaflet's stacking
           context so it's always visible regardless of tile animation / reflow. */}
      <div className="px-4 py-2 border-t border-succession-border flex items-center gap-2">
        <span className="text-xs text-succession-text-secondary">{intl.formatMessage({ id: 'succession.launchpad.heatmap_legend_aum' })}</span>
        <span className="text-xs text-succession-text-tertiary">{intl.formatMessage({ id: 'succession.launchpad.heatmap_legend_low' })}</span>
        <div
          className="h-2 w-24 rounded-full"
          style={{
            background: `linear-gradient(to right, ${getHeatColor(0, getBrandRgb())}, ${getHeatColor(1, getBrandRgb())})`,
          }}
        />
        <span className="text-xs text-succession-text-tertiary">{intl.formatMessage({ id: 'succession.launchpad.heatmap_legend_high' })}</span>
        <span className="ml-auto text-xs text-succession-text-secondary">
          {intl.formatMessage({ id: 'succession.launchpad.heatmap_bubble_hint' })}
        </span>
      </div>

      <HeatmapTooltip tooltip={tooltip} />
    </div>
  );
};

export default SellerHeatMapLaunchpad;
